import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { subscribeToPush } from '../services/webPushService';

/**
 * Auto-subscribes the signed-in user to Web Push notifications.
 *
 * Safe to call in any top-level component — registration is idempotent (the
 * browser returns the same endpoint and the backend `update_or_create`s on it).
 *
 * `client` / `base` default to the ERP pair. The customer portal passes its own
 * axios instance and prefix, because a `user_type='CUSTOMER'` token is refused
 * on `/api/push/` at the edge; `signedIn` lets it supply its own session test,
 * since the portal keeps no redux auth slice.
 */
export default function useWebPush({ client, base, signedIn } = {}) {
  const reduxUser = useSelector((state) => state.auth?.user);
  const user = signedIn === undefined ? reduxUser : signedIn;
  const subscribedRef = useRef(false);

  const subscribe = useCallback(async () => {
    if (subscribedRef.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    // Only attempt if permission was already granted or is 'default' (will prompt)
    if (Notification.permission === 'denied') return;

    try {
      await subscribeToPush({ client, base });
      subscribedRef.current = true;
    } catch (err) {
      // Non-fatal — in-app WebSocket notifications still work
      console.warn('[WebPush] Subscription failed:', err);
    }
  }, [client, base]);

  useEffect(() => {
    if (user) {
      // Defer until after first paint so it doesn't block the login flow
      const timer = setTimeout(subscribe, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [user, subscribe]);
}
